/**
 * What the desk may do with a referral, and what it will cost, before the
 * desk confirms. Mirrors the backend's planAdvance so the preview is the
 * figure the server will store:
 *   - moves come only from the shared stage machine (canTransition);
 *   - a quote taken earlier is reused unless price, loan or property type
 *     change (a fee-card edit never rewrites a quoted fee);
 *   - a pre-price stage payout is priced from the current schedule.
 */
import { PAYABLE_STAGES, PRICE_REQUIRED_STAGES, PartnerFeeScheduleSchema, canTransition, nextStage, quoteReferralFee, quoteStagePayout, } from './schemas';
export function legalMoves(from) {
    const candidates = [nextStage(from), 'contacted', 'lapsed'];
    const out = [];
    for (const c of candidates) {
        if (c && !out.includes(c) && canTransition(from, c))
            out.push(c);
    }
    return out;
}
export function needsPrice(to) {
    return PRICE_REQUIRED_STAGES.includes(to);
}
export function previewAdvance(rawSchedule, subject, to, inputs) {
    const price = inputs.purchasePriceCents ?? subject.purchasePriceCents;
    const loan = inputs.loanAmountCents ?? subject.loanAmountCents ?? null;
    const propertyType = inputs.propertyType ?? subject.propertyType;
    const parse = () => PartnerFeeScheduleSchema.safeParse(rawSchedule);
    try {
        if (needsPrice(to)) {
            if (price === null || price <= 0)
                return { kind: 'error', message: 'Enter the purchase price to see the fee.' };
            if (propertyType === null)
                return { kind: 'error', message: 'Choose the property type to see the fee.' };
            const current = subject.feeQuote ?? null;
            const changed = current === null ||
                price !== subject.purchasePriceCents ||
                loan !== (subject.loanAmountCents ?? null) ||
                propertyType !== subject.propertyType;
            if (!changed && current) {
                return { kind: 'quote', quote: current, stagePayout: current.payouts[to] ?? null, reused: true };
            }
            const parsed = parse();
            if (!parsed.success)
                return { kind: 'error', message: 'There is no valid fee schedule yet. Set one in Fee schedule first.' };
            const quote = quoteReferralFee(parsed.data, { purchasePriceCents: price, loanAmountCents: loan, propertyType });
            return { kind: 'quote', quote, stagePayout: quote.payouts[to] ?? null, reused: false };
        }
        if (PAYABLE_STAGES.includes(to)) {
            const parsed = parse();
            if (!parsed.success)
                return { kind: 'error', message: 'There is no valid fee schedule yet. Set one in Fee schedule first.' };
            const payout = quoteStagePayout(parsed.data, to, {
                purchasePriceCents: price ?? null,
                loanAmountCents: loan,
                propertyType,
            });
            return { kind: 'stage', stagePayout: payout };
        }
        return { kind: 'none' };
    }
    catch (err) {
        return { kind: 'error', message: err instanceof Error ? err.message : 'This fee could not be worked out.' };
    }
}
